import { useNavigate } from 'react-router-dom'

import { FormikHelpers } from 'formik'
import {
  BankingValueObjectsCardTypeEnum,
  CardholderInformationDocument,
  CreatePhysicalBankingCardCardholderDataInput,
  CreatePhysicalBankingCardShippingInput,
  CreatePhysicalCardDocument,
  CreatePhysicalCardMutation,
  CreateSingleUseBankingCardCardholderDataInput,
  CreateSingleUseCardDocument,
  CreateSingleUseCardMutation,
  CreateVirtualBankingCardCardholderDataInput,
  CreateVirtualCardDocument,
  CreateVirtualCardMutation,
  NewCardLegalEntityFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { cardAmplitudeData } from 'modules/banking/lib/cards/cardAmplitudeData'
import { getPhoneCode } from 'modules/expenses/lib/countryCodes'
import { isMobilePhoneNumber, isValidPhoneNumber } from 'modules/expenses/lib/phoneNumber'

export type CreateCardForm = {
  canCreateExpenses: boolean
  cardType?: BankingValueObjectsCardTypeEnum
  employeeId?: number | null
  cardholderData:
    | CreatePhysicalBankingCardCardholderDataInput
    | CreateVirtualBankingCardCardholderDataInput
    | CreateSingleUseBankingCardCardholderDataInput
  shipping: CreatePhysicalBankingCardShippingInput
  shippingContactPerson?: number
  spendingAmount: string
  alias?: string
}

type CreateCardResult =
  | Result<
      Omit<CreateSingleUseCardMutation['bankingMutations']['createSingleUseBankingCard'], 'errors'>
    >
  | Result<
      Omit<CreateVirtualCardMutation['bankingMutations']['createVirtualBankingCard'], 'errors'>
    >
  | Result<
      Omit<CreatePhysicalCardMutation['bankingMutations']['createPhysicalBankingCard'], 'errors'>
    >

const handleCreateCardResult = (result: CreateCardResult, onSuccess: (cardId: number) => void) => {
  if (result.success) {
    if (result.data?.card) {
      track(
        trackingEvents.expenses.createNewCardSubmit,
        cardAmplitudeData(result.data.card, {
          context: 'company',
          origin: 'create_card',
        })
      )
    }
    app.setFlash({
      type: 'correct',
      message: i18n.t('software_management.software_setups.cards.alerts.create_success'),
    })

    if (result.data?.card) {
      onSuccess(result.data.card.id)
    }
  } else {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('software_management.software_setups.cards.errors.error_create'),
    })
  }
}

export const useCardForm = (mainLegalEntity?: NewCardLegalEntityFragment) => {
  const [createVirtualCard, { loading: createVirtualCardLoading }] = useMutation(
    CreateVirtualCardDocument,
    {
      extractorFn: (data: CreateVirtualCardMutation) =>
        data.bankingMutations.createVirtualBankingCard,
    }
  )
  const [createPhysicalCard, { loading: createPhysicalCardLoading }] = useMutation(
    CreatePhysicalCardDocument,
    {
      extractorFn: (data: CreatePhysicalCardMutation) =>
        data.bankingMutations.createPhysicalBankingCard,
    }
  )
  const [createSingleUseCard, { loading: createSingleUseCardLoading }] = useMutation(
    CreateSingleUseCardDocument,
    {
      extractorFn: (data: CreateSingleUseCardMutation) =>
        data.bankingMutations.createSingleUseBankingCard,
    }
  )
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const onSuccess = (cardId: number) =>
    navigate({ pathname: navigation.finance.cards.fullPath(), search: `?showCard=${cardId}` })
  const companyId = useCurrentSession().company.id

  const form = useForm<CreateCardForm>({
    defaultValues: {
      cardholderData: {
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        email: '',
        phoneCountryCode: '',
        phoneNumber: '',
      },
      shipping: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        companyEmployeeFullName: '',
        companyName: '',
        companyPhoneCountryCode: '',
        companyPhoneNumber: '',
        country: '',
        postalCode: '',
        sendingType: 'cardholder',
        state: '',
      },
      alias: '',
      spendingAmount: '',
      shippingContactPerson: undefined,
      canCreateExpenses: false,
    },
    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: false,
    validate: (data: CreateCardForm) => {
      const errors: FormErrors<CreateCardForm> = {}
      if (!cardholder) {
        const cardholderErrors: FormErrors<
          | CreatePhysicalBankingCardCardholderDataInput
          | CreateVirtualBankingCardCardholderDataInput
          | CreateSingleUseBankingCardCardholderDataInput
        > = {}
        const fullName = `${data.cardholderData.firstName} ${data.cardholderData.lastName}`
        if (fullName.length > 24) {
          cardholderErrors.firstName = i18n.t('expenses_v2.forms.errors.full_name.too_long')
          cardholderErrors.lastName = i18n.t('expenses_v2.forms.errors.full_name.too_long')
        }
        if (
          !isMobilePhoneNumber(
            data.cardholderData.phoneCountryCode,
            data.cardholderData.phoneNumber
          )
        ) {
          cardholderErrors.phoneNumber = i18n.t('expenses_v2.forms.errors.phone.not_mobile')
        } else if (
          !isValidPhoneNumber(data.cardholderData.phoneCountryCode, data.cardholderData.phoneNumber)
        ) {
          cardholderErrors.phoneNumber = i18n.t('expenses_v2.forms.errors.phone.invalid')
        }

        if (!data.cardholderData.dateOfBirth) {
          cardholderErrors.dateOfBirth = i18n.t(
            'software_management.software_setups.cards.errors.birth_date_mandatory'
          )
        } else if (
          new Date(data.cardholderData.dateOfBirth) >
          new Date(new Date().setFullYear(new Date().getFullYear() - 18))
        ) {
          cardholderErrors.dateOfBirth = i18n.t(
            'software_management.software_setups.cards.errors.birth_date_invalid'
          )
        }

        if (!data.cardholderData.firstName) {
          cardholderErrors.firstName = i18n.t('common.errors.required')
        }

        if (!data.cardholderData.lastName) {
          cardholderErrors.lastName = i18n.t('common.errors.required')
        }

        if (!data.cardholderData.phoneNumber) {
          cardholderErrors.phoneNumber = i18n.t(
            'software_management.software_setups.cards.errors.mobile_phone_mandatory'
          )
        }

        if (!data.cardholderData.phoneCountryCode) {
          cardholderErrors.phoneCountryCode = i18n.t(
            'software_management.software_setups.cards.errors.mobile_phone_mandatory'
          )
        }

        if (Object.keys(cardholderErrors).length > 0) {
          errors.cardholderData = cardholderErrors
        }
      }

      if (data.cardType === 'single_use') {
        if (!data.spendingAmount) {
          errors.spendingAmount = i18n.t(
            'software_management.software_setups.cards.errors.spending_amount_mandatory'
          )
        }

        if (parseInt(data.spendingAmount) < 0) {
          errors.spendingAmount = i18n.t(
            'software_management.software_setups.cards.errors.spending_amount_positive'
          )
        }
      }

      if (data.cardType === 'physical') {
        const shippingErrors: FormErrors<CreatePhysicalBankingCardShippingInput> = {}

        if (data.shipping.sendingType === 'company') {
          if (!data.shippingContactPerson) {
            errors.shippingContactPerson = i18n.t('common.errors.required')
          }
          if (
            !data.shipping.companyPhoneCountryCode ||
            data.shipping.companyPhoneCountryCode?.length < 2
          ) {
            shippingErrors.companyPhoneCountryCode = i18n.t('common.errors.required')
          }
          if (!data.shipping.companyPhoneNumber || data.shipping.companyPhoneNumber?.length === 0) {
            shippingErrors.companyPhoneNumber = i18n.t('common.errors.required')
          }
          if (!data.shipping.companyName || data.shipping.companyName?.length === 0) {
            shippingErrors.companyName = i18n.t('common.errors.required')
          }
          if (
            !data.shipping.companyEmployeeFullName ||
            data.shipping.companyEmployeeFullName?.length === 0
          ) {
            shippingErrors.companyEmployeeFullName = i18n.t('common.errors.required')
          }
        }

        if (data.shipping.addressLine1.length === 0) {
          shippingErrors.addressLine1 = i18n.t('common.errors.required')
        }

        if (!data.shipping.state || data.shipping.state.length === 0) {
          shippingErrors.state = i18n.t('common.errors.required')
        }

        if (data.shipping.city.length === 0) {
          shippingErrors.city = i18n.t('common.errors.required')
        }

        if (data.shipping.country.length === 0) {
          shippingErrors.country = i18n.t('common.errors.required')
        }

        if (data.shipping.postalCode.length === 0) {
          shippingErrors.postalCode = i18n.t('common.errors.required')
        }

        if (data.shipping.addressLine1.length > 50) {
          shippingErrors.addressLine1 = i18n.t('expenses_v2.forms.errors.address.too_long')
        }

        if (data.shipping.addressLine2 && data.shipping.addressLine2.length > 50) {
          shippingErrors.addressLine2 = i18n.t('expenses_v2.forms.errors.address.too_long')
        }

        if (data.shipping.companyName && data.shipping.companyName.length > 50) {
          shippingErrors.companyName = i18n.t('expenses_v2.forms.errors.address.too_long')
        }

        if (Object.keys(shippingErrors).length > 0) {
          errors.shipping = shippingErrors
        }
      }

      if (data.employeeId && !data.canCreateExpenses) {
        errors.employeeId = i18n.t(
          'expenses_v2.forms.errors.employee.not_allowed_to_create_expenses'
        )
      }

      return errors
    },
    onSubmit: async (data: CreateCardForm, { setErrors }: FormikHelpers<CreateCardForm>) => {
      if (!data.employeeId) {
        throw new Error('Employee is required')
      }
      if (!data.cardType) {
        throw new Error('Card type is required')
      }

      const commonVariables = {
        employeeId: data.employeeId + '',
        alias: data.alias || '',
        companyId,
        cardholderData: cardholder ? undefined : data.cardholderData,
      }

      if (data.cardType === 'virtual') {
        const unhandledResult = await createVirtualCard({
          variables: commonVariables,
        })
        const result = handleResult(unhandledResult, (errors) => setErrors(errors))
        handleCreateCardResult(result, onSuccess)
        return result
      } else if (data.cardType === 'physical') {
        const unhandledResult = await createPhysicalCard({
          variables: {
            ...commonVariables,
            shipping: data.shipping,
          },
        })
        const result = handleResult(unhandledResult, (errors) => setErrors(errors))
        handleCreateCardResult(result, onSuccess)
        return result
      } else if (data.cardType === 'single_use') {
        const amountInCents = parseInt(data.spendingAmount) * 100
        const unhandledResult = await createSingleUseCard({
          variables: {
            ...commonVariables,
            amountLimitCents: amountInCents,
          },
        })
        const result = handleResult(unhandledResult, (errors) => setErrors(errors))
        handleCreateCardResult(result, onSuccess)
        return result
      } else {
        return data.cardType satisfies never
      }
    },
  })

  const { data: cardholderData, networkActivity: loadingCardholder } = useQuery(
    CardholderInformationDocument,
    {
      variables: { employeeId: Number(form.values.employeeId), creationSource: 'expenses' },
      skip: !form.values.employeeId,
    }
  )
  const selectedEmployeeInformation = cardholderData?.employees?.employee
  const selectedLegalEntity = hasFeature('dev_expenses_multi_accounts')
    ? selectedEmployeeInformation?.legalEntity
    : mainLegalEntity
  const cardholder = selectedEmployeeInformation?.bankingCardholder

  const cardsAvailableInCountry =
    (selectedLegalEntity?.bankingProviderConfigurationsConnection?.totalCount || 0) > 0
  const hasAccount = (selectedLegalEntity?.bankingAccountsConnection?.totalCount || 0) > 0

  const buildCardholderInformation = () => {
    if (selectedEmployeeInformation) {
      const data = {
        dateOfBirth: selectedEmployeeInformation.birthdayOn || '',
        firstName: selectedEmployeeInformation.firstName || '',
        lastName: selectedEmployeeInformation.lastName || '',
        phoneCountryCode:
          selectedEmployeeInformation.bankingCardholder?.phoneCountryCode ||
          selectedEmployeeInformation.phoneCode ||
          getPhoneCode(selectedEmployeeInformation.country),
        phoneNumber:
          selectedEmployeeInformation.bankingCardholder?.phoneNumber ||
          selectedEmployeeInformation.phoneNumber ||
          '',
        email: selectedEmployeeInformation.email || '',
      }

      return data
    } else {
      return form.values.cardholderData
    }
  }

  const buildShippingInformation = () => {
    if (selectedEmployeeInformation && form.values.cardType === 'physical') {
      if (form.touched.shipping) {
        form.touched.shipping = {}
      }
      if (form.values.shipping.sendingType == 'company') {
        return {
          ...form.values.shipping,
          addressLine1: selectedEmployeeInformation?.legalEntity?.addressLine1 ?? '',
          addressLine2: selectedEmployeeInformation?.legalEntity?.addressLine2 ?? '',
          city: selectedEmployeeInformation?.legalEntity?.city ?? '',
          country: selectedEmployeeInformation?.legalEntity?.country ?? '',
          postalCode: selectedEmployeeInformation?.legalEntity?.postalCode ?? '',
          state: selectedEmployeeInformation?.legalEntity?.state ?? '',
          companyName:
            selectedEmployeeInformation?.legalEntity?.legalName ||
            selectedEmployeeInformation?.company?.name ||
            '',
          companyPhoneCountryCode: getPhoneCode(selectedEmployeeInformation?.legalEntity?.country),
          companyPhoneNumber: '',
        }
      } else if (form.values.shipping.sendingType == 'cardholder') {
        return {
          ...form.values.shipping,
          addressLine1: selectedEmployeeInformation?.addressLine1 ?? '',
          addressLine2: selectedEmployeeInformation?.addressLine2 ?? '',
          city: selectedEmployeeInformation?.city ?? '',
          country: selectedEmployeeInformation?.country ?? '',
          postalCode: selectedEmployeeInformation?.postalCode ?? '',
          state: selectedEmployeeInformation?.state ?? '',
          companyPhoneNumber: selectedEmployeeInformation?.phoneNumber ?? '',
          companyPhoneCountryCode:
            selectedEmployeeInformation?.phoneCode ||
            getPhoneCode(selectedEmployeeInformation?.country),
        }
      } else {
        return form.values.shipping
      }
    } else {
      return form.values.shipping
    }
  }

  useUnsafeEffect(() => {
    form.setValues(
      {
        ...form.values,
        cardholderData: buildCardholderInformation(),
        shipping: buildShippingInformation(),
        canCreateExpenses:
          selectedEmployeeInformation?._policies?.createExpenseCardFor.can ?? false,
      },
      true
    )
  }, [selectedEmployeeInformation])

  useUnsafeEffect(() => {
    form.setValues(
      {
        ...form.values,
        shipping: buildShippingInformation(),
        shippingContactPerson: undefined,
      },
      true
    )
  }, [form.values.shipping.sendingType, form.values.cardType])

  const hasErrors = Object.keys(form.errors).length > 0

  const submitDisabled =
    form.isSubmitting ||
    createVirtualCardLoading ||
    createPhysicalCardLoading ||
    createSingleUseCardLoading ||
    !hasAccount ||
    !cardsAvailableInCountry ||
    hasErrors ||
    !selectedEmployeeInformation

  return {
    form,
    loading: createVirtualCardLoading,
    loadingCardholder: loadingCardholder,
    selectedEmployeeInformation,
    selectedLegalEntity,
    hasAccount,
    cardsAvailableInCountry,
    submitDisabled,
  }
}
